import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createIssueOrder,
  getAllAppointments,
  getOrders,
  getOrganizations,
  getPortalReportHistory,
  getWarehouseList,
  listAllPortalStates,
  manualUpdateCustomerOrderStatus,
  patchIssueOrder,
  retrieveIssueSummary,
  shareIssueToOperation,
  updateOrganization,
} from "../dispatcher/portalDispatcher";

interface SubOrgProductState {
  _id: string;
  itemCode: string;
  subOrganizationId: string;
  assignedBy: string;
}

interface PortalState {
  loading: boolean;
  searching: boolean;
  orders: any[];

  loadingAction: boolean;
  loadingModalAction: boolean;
  order: any;

  totalCount: any;
  totalPages: any;
  pageSize: number;
  page: number;
  sort: any;
  status: string;
  searchQuery: string;
  categoryFilter: string;
  subOrganizationFilter: string;
  sortBy: string;
  requestType: string;
  dateFilter: string;

  issues: {
    loading: boolean;
    searching: boolean;
    orders: any[];

    loadingAction: boolean;
    loadingModalAction: boolean;
    order: any;

    totalCount: any;
    totalPages: any;
    pageSize: number;
    page: number;
    sort: any;
    status: string;
    searchQuery: string;
    categoryFilter: string;
    subOrganizationFilter: string;
    sortBy: string;
    issueType: string;
  };

  appointments: {
    loading: boolean;
    appointments: any[];
    loadingAction: boolean;
    appointment: any;
  };

  warehouses: {
    loading: boolean;
    warehouses: any[];
    loadingAction: boolean;
    warehouse: any;
  };

  admin: {
    // States State
    states: {
      states: any[];
      state: any;
      loading: boolean;
      searching: boolean;
      loadingAction: boolean;
      hasFetched: boolean;

      totalCount: any;
      totalPages: any;
      pageSize: number;
      page: number;
      sort: any;

      searchQuery: string;
      sortBy: string;
    };

    // Organizations
    organizations: {
      loading: boolean;
      organizations: any[];

      organization: any;
      organizationCount: number;
      loadingActionOrganization: boolean;

      totalCount: any;
      totalPages: any;
      pageSize: number;
      page: number;
      sort: any;
      status: string;
      searchQuery: string;
      categoryFilter: string;
      sortBy: string;

      products: {
        loading: boolean;
        loadingAction: boolean;
        products: SubOrgProductState[];
        totalCount: any;
        totalPages: any;
        pageSize: number;
        page: number;
        sort: any;
        searchQuery: string;
        sortBy: string;

        hasFetched: boolean;
      };
    };
  };
}

const initialState: PortalState = {
  orders: [],
  loading: false,
  searching: false,
  loadingAction: false,

  loadingModalAction: false,

  order: null,

  totalCount: null,
  totalPages: null,
  pageSize: 20,
  page: 1,
  sort: {
    key: "createdAt",
    direction: "desc",
  },
  status: "all",

  // Add new state for search and filters
  searchQuery: "",
  categoryFilter: "all",
  subOrganizationFilter: "all",
  sortBy: "created-desc",
  requestType: "all",
  dateFilter: "Next 14 days",

  issues: {
    orders: [],
    loading: false,
    searching: false,
    loadingAction: false,

    loadingModalAction: false,

    order: null,

    totalCount: null,
    totalPages: null,
    pageSize: 20,
    page: 1,
    sort: {
      key: "createdAt",
      direction: "desc",
    },
    status: "all",

    // Add new state for search and filters
    searchQuery: "",
    categoryFilter: "all",
    subOrganizationFilter: "all",
    sortBy: "created-desc",
    issueType: "all",
  },

  appointments: {
    appointment: null,
    appointments: [],
    loading: false,
    loadingAction: false,
  },

  warehouses: {
    warehouse: null,
    warehouses: [],
    loading: false,
    loadingAction: false,
  },

  // Portal Superadmin States
  admin: {
    organizations: {
      loading: false,
      organizations: [],

      organization: null,
      organizationCount: 0,
      loadingActionOrganization: false,

      totalCount: null,
      totalPages: null,
      pageSize: 10,
      page: 1,
      sort: {
        key: "created_at",
        direction: "desc",
      },
      status: "all",

      // Add new state for search and filters
      searchQuery: "",
      categoryFilter: "all",
      sortBy: "created-desc",

      // Admin Products For Assignment to individual  organization
      products: {
        loading: false,
        products: [],
        loadingAction: false,
        totalCount: null,
        totalPages: null,
        pageSize: 10,
        page: 1,
        sort: {
          key: "created_at",
          direction: "desc",
        },
        searchQuery: "",
        sortBy: "created-desc",

        hasFetched: false,
      },
    },

    // States State
    states: {
      states: [],
      state: null,
      loading: false,
      searching: false,
      loadingAction: false,
      hasFetched: false,

      totalCount: null,
      totalPages: null,
      pageSize: 15,
      page: 1,
      sort: {
        key: "createdAt",
        direction: "desc",
      },

      searchQuery: "",
      sortBy: "created-desc",
    },
  },
};

// Create a slice for actions and reducers
export const portalSlice = createSlice({
  name: "portal",
  initialState,
  reducers: {
    setOrderSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
      state.page = 1; // reset page when searching
      state.searching = true;
    },
    setOrderCategoryFilter: (state, action) => {
      state.categoryFilter = action.payload;
      state.page = 1;
    },
    setSubOrganizationFilter: (state, action) => {
      state.subOrganizationFilter = action.payload;
      state.page = 1;
    },
    setOrderSortBy: (state, action) => {
      state.sortBy = action.payload;
      state.page = 1;
    },
    setOrderPage: (state, action) => {
      state.page = action.payload;
    },
    setRequestType: (state, action) => {
      state.requestType = action.payload;
    },
    setOrderDateFilter: (state, action) => {
      state.dateFilter = action.payload;
      state.page = 1;
    },

    // Issues Report
    setIssueSearchQuery: (state, action) => {
      state.issues.searchQuery = action.payload;
      state.issues.page = 1; // reset page when searching
      state.issues.searching = true;
    },
    setIssueCategoryFilter: (state, action) => {
      state.issues.categoryFilter = action.payload;
      state.issues.page = 1;
    },
    setIssueSubOrganizationFilter: (state, action) => {
      state.issues.subOrganizationFilter = action.payload;
      state.issues.page = 1;
    },
    setIssueSortBy: (state, action) => {
      state.issues.sortBy = action.payload;
      state.issues.page = 1;
    },
    setIssuePage: (state, action) => {
      state.issues.page = action.payload;
    },
    setIssueType: (state, action) => {
      state.issues.issueType = action.payload;
    },

    // Add organization filter actions
    setOrganizationSearchQuery: (state, action) => {
      state.admin.organizations.searchQuery = action.payload;
      state.admin.organizations.page = 1; // reset page when searching
    },
    setOrganizationCategoryFilter: (state, action) => {
      state.admin.organizations.categoryFilter = action.payload;
      state.admin.organizations.page = 1;
    },
    setOrganizationSortBy: (state, action) => {
      state.admin.organizations.sortBy = action.payload;
      state.admin.organizations.page = 1;
    },
    setOrganizationPage: (state, action) => {
      state.admin.organizations.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrders.pending, (state) => {
        if (!state.searching) {
          state.loading = true;
        }
      })
      .addCase(getOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.data.orders;
        state.totalCount = action.payload.data.pagination.total;
        state.totalPages = action.payload.data.pagination.totalPages;
        state.pageSize = action.payload.data.pagination.pageSize;
        state.page = action.payload.data.pagination.page;
        state.searching = false;
      })
      .addCase(getOrders.rejected, (state) => {
        state.loading = false;
        state.searching = false;
      })

      // Get Shipments
      .addCase(getPortalReportHistory.pending, (state) => {
        if (!state.searching) {
          state.issues.loading = true;
        }
      })
      .addCase(getPortalReportHistory.fulfilled, (state, action) => {
        state.issues.loading = false;
        state.issues.orders = action.payload.data.issues;
        state.issues.totalCount = action.payload.data.pagination.total;
        state.issues.totalPages = action.payload.data.pagination.totalPages;
        state.issues.pageSize = action.payload.data.pagination.pageSize;
        state.issues.page = action.payload.data.pagination.page;
        state.issues.searching = false;
      })
      .addCase(getPortalReportHistory.rejected, (state) => {
        state.issues.loading = false;
        state.issues.searching = false;
      })

      // Retrieve Issue Sumamry
      .addCase(retrieveIssueSummary.pending, (state) => {
        state.issues.loading = true;
      })
      .addCase(retrieveIssueSummary.fulfilled, (state, action) => {
        state.issues.loading = false;
        state.issues.order = action.payload.data;
      })
      .addCase(retrieveIssueSummary.rejected, (state) => {
        state.issues.loading = false;
      })

      // Create/Reply Issue Order
      .addCase(createIssueOrder.pending, (state) => {
        state.issues.loadingAction = true;
      })
      .addCase(createIssueOrder.fulfilled, (state, action) => {
        state.issues.loadingAction = false;
      })
      .addCase(createIssueOrder.rejected, (state) => {
        state.issues.loadingAction = false;
      })

      // Update Issue Order Status (CLOSE, RE-OPEN)
      .addCase(patchIssueOrder.pending, (state) => {
        state.issues.loadingAction = true;
      })
      .addCase(patchIssueOrder.fulfilled, (state, action) => {
        state.issues.loadingAction = false;
        const { _id: issueId, status } = action.payload.data;

        // Find the issue by ID and update its status
        const issueIndex = state.issues.orders.findIndex(
          (issue: any) => issue._id === issueId,
        );
        if (issueIndex !== -1) {
          state.issues.orders[issueIndex].status = status;
        }
      })

      .addCase(patchIssueOrder.rejected, (state) => {
        state.issues.loadingAction = false;
      })

      .addCase(shareIssueToOperation.pending, (state) => {
        state.issues.loadingAction = true;
      })
      .addCase(shareIssueToOperation.fulfilled, (state, action) => {
        state.issues.loadingAction = false;
      })
      .addCase(shareIssueToOperation.rejected, (state) => {
        state.issues.loadingAction = false;
      })

      .addCase(manualUpdateCustomerOrderStatus.pending, (state) => {
        state.loadingAction = true;
      })
      .addCase(manualUpdateCustomerOrderStatus.fulfilled, (state, action) => {
        state.loadingAction = false;
      })
      .addCase(manualUpdateCustomerOrderStatus.rejected, (state) => {
        state.loadingAction = false;
      })

      // Portal Delivery Appointments
      .addCase(getAllAppointments.pending, (state) => {
        state.appointments.loading = true;
      })
      .addCase(getAllAppointments.fulfilled, (state, action) => {
        state.appointments.loading = false;
        state.appointments.appointments = action.payload.data.appointments;
      })
      .addCase(getAllAppointments.rejected, (state) => {
        state.appointments.loading = false;
      })

      // Portal Delivery Warehouses
      .addCase(getWarehouseList.pending, (state) => {
        state.warehouses.loading = true;
      })
      .addCase(getWarehouseList.fulfilled, (state, action) => {
        state.warehouses.loading = false;
        state.warehouses.warehouses = action.payload.data.warehouses;
      })
      .addCase(getWarehouseList.rejected, (state) => {
        state.warehouses.loading = false;
      })

      // List All Portal States
      .addCase(listAllPortalStates.pending, (state) => {
        if (!state.admin.states.searching) {
          state.admin.states.loading = true;
        }
      })
      .addCase(listAllPortalStates.fulfilled, (state, action) => {
        state.admin.states.loading = false;
        state.admin.states.states = action.payload.data.states;
        state.admin.states.searching = false;
      })
      .addCase(listAllPortalStates.rejected, (state, action) => {
        state.admin.states.loading = false;
        state.admin.states.searching = false;
      })

      // Organizations
      // Get Organizations
      .addCase(getOrganizations.pending, (state) => {
        state.admin.organizations.loading = true;
      })
      .addCase(getOrganizations.fulfilled, (state, action) => {
        state.admin.organizations.loading = false;
        state.admin.organizations.organizations =
          action.payload.data.organizations;
        state.admin.organizations.totalCount =
          action.payload.data.pagination.total;
        state.admin.organizations.totalPages =
          action.payload.data.pagination.totalPages;
        state.admin.organizations.pageSize =
          action.payload.data.pagination.pageSize;
        state.admin.organizations.page = action.payload.data.pagination.page;
      })
      .addCase(getOrganizations.rejected, (state) => {
        state.admin.organizations.loading = false;
      })

      // Update Organization
      .addCase(updateOrganization.pending, (state) => {
        state.admin.organizations.loadingActionOrganization = true;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.admin.organizations.loadingActionOrganization = false;
        // Update the organization in the list
        const index = state.admin.organizations.organizations.findIndex(
          (org) => org.id === action.payload.data.id,
        );
        if (index !== -1) {
          state.admin.organizations.organizations[index] = action.payload.data;
        }
        // Update single organization if it's the same one
        if (
          state.admin.organizations.organization?.id === action.payload.data.id
        ) {
          state.admin.organizations.organization = action.payload.data;
        }
      })
      .addCase(updateOrganization.rejected, (state) => {
        state.admin.organizations.loadingActionOrganization = false;
      });
  },
});

// Export actions
export const {
  setOrderCategoryFilter,
  setOrderPage,
  setOrderSearchQuery,
  setOrderSortBy,
  setRequestType,
  setOrderDateFilter,
  setSubOrganizationFilter,

  setIssueCategoryFilter,
  setIssuePage,
  setIssueSearchQuery,
  setIssueSortBy,
  setIssueSubOrganizationFilter,
  setIssueType,

  setOrganizationCategoryFilter,
  setOrganizationPage,
  setOrganizationSearchQuery,
  setOrganizationSortBy,
} = portalSlice.actions;

// Export reducer
export default portalSlice.reducer;
